import { RadioCardItem, RadioCardRoot } from '@/components/ui/radio-card';
import { Switch } from '@/components/ui/switch';
import { HStack, Text, VStack } from '@chakra-ui/react';
import {
  CODE_SEGMENT_MAX_LENGTH,
  CodePatternPart,
  CodePatternSeparator,
  sanitizeCodeSegment,
  type CodePattern,
  type CodePatternPartConfig,
} from '@fluenceur/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Preset format definitions.
 * A preset says which parts a code is made of and in which order; the separator
 * and the case are the brand's own choices, kept across preset changes.
 */
const PRESETS: { id: string; parts: CodePatternPartConfig[] }[] = [
  {
    id: 'brand-affiliate',
    parts: [{ type: CodePatternPart.BrandName }, { type: CodePatternPart.CreatorName }],
  },
  {
    id: 'affiliate-brand',
    parts: [{ type: CodePatternPart.CreatorName }, { type: CodePatternPart.BrandName }],
  },
  {
    id: 'affiliate-discount',
    parts: [{ type: CodePatternPart.CreatorName }, { type: CodePatternPart.DiscountPercent }],
  },
  {
    id: 'brand-affiliate-discount',
    parts: [
      { type: CodePatternPart.BrandName },
      { type: CodePatternPart.CreatorName },
      { type: CodePatternPart.DiscountPercent },
    ],
  },
  {
    id: 'affiliate-random',
    parts: [{ type: CodePatternPart.CreatorName }, { type: CodePatternPart.RandomCode }],
  },
];

/**
 * Generates a preview discount code from a pattern and context.
 * Pure function, no side effects.
 */
export function generatePreviewCode(
  pattern: CodePattern,
  context: { brandName: string; discountValue: number },
  creatorName = 'EMMA'
): string {
  const parts = pattern.parts.map((part) => {
    switch (part.type) {
      case CodePatternPart.CreatorName:
        return sanitizeCodeSegment(creatorName, CODE_SEGMENT_MAX_LENGTH.creatorName);
      case CodePatternPart.BrandName:
        return sanitizeCodeSegment(context.brandName);
      case CodePatternPart.DiscountPercent:
        return String(Math.round(context.discountValue));
      case CodePatternPart.RandomCode:
        return 'A1B2';
      case CodePatternPart.CustomText:
        return sanitizeCodeSegment(part.value || '', CODE_SEGMENT_MAX_LENGTH.customText);
      default:
        return '';
    }
  });
  const actualSeparator = pattern.separator === CodePatternSeparator.None ? '' : pattern.separator;
  const joined = parts.join(actualSeparator);
  const sanitized = joined.replace(/[^A-Za-z0-9_-]/g, '');
  return pattern.uppercase ? sanitized.toUpperCase() : sanitized;
}

/**
 * Find which preset matches a given CodePattern, or default to first preset.
 *
 * Only the parts are compared: the separator is a choice the brand makes on top
 * of a preset, so matching on it would leave the list with nothing selected as
 * soon as the dashes are dropped.
 */
export function findPresetId(pattern: CodePattern): string {
  const match = PRESETS.find((preset) => {
    if (preset.parts.length !== pattern.parts.length) return false;
    return preset.parts.every((p, i) => p.type === pattern.parts[i].type);
  });
  return match?.id ?? PRESETS[0].id;
}

/** One labelled option of the format, so both switches read the same way. */
const PatternOptionSwitch: React.FC<{
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}> = ({ label, checked, onCheckedChange }) => (
  <HStack justify="space-between" align="center">
    <Text fontSize="sm" fontWeight="medium">
      {label}
    </Text>
    <Switch
      size="sm"
      colorPalette="primary"
      checked={checked}
      inputProps={{ 'aria-label': label }}
      onCheckedChange={(e) => onCheckedChange(e.checked)}
    />
  </HStack>
);

interface DiscountCodePatternBuilderProps {
  value: CodePattern;
  onChange: (pattern: CodePattern) => void;
  brandName: string;
  discountValue?: number;
}

const DiscountCodePatternBuilder: React.FC<DiscountCodePatternBuilderProps> = ({
  value: pattern,
  onChange,
  brandName,
  discountValue = 10,
}) => {
  const { t } = useTranslation();
  const prefix = 'programs.modals.editDiscountCode';

  const selectedPresetId = useMemo(() => findPresetId(pattern), [pattern]);

  // `Underscore` exists in the enum but no preset offers it. A pattern saved
  // with it reads as separated, and turning the switch off then on again brings
  // the code back with dashes: one switch, two states, no third one to explain.
  const separated = pattern.separator !== CodePatternSeparator.None;

  const handlePresetChange = (presetId: string) => {
    const preset = PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    onChange({
      // Copied, never aliased: the pattern leaves the component and the preset
      // list has to stay the same for the next brand that opens the modal.
      parts: preset.parts.map((part) => ({ ...part })),
      separator: pattern.separator,
      uppercase: pattern.uppercase,
    });
  };

  const previewContext = useMemo(() => ({ brandName, discountValue }), [brandName, discountValue]);

  return (
    <VStack gap={4} align="stretch">
      <RadioCardRoot
        size="sm"
        colorPalette="primary"
        value={selectedPresetId}
        onValueChange={(details) => details.value && handlePresetChange(details.value)}
      >
        <VStack gap={2} align="stretch">
          {PRESETS.map((preset) => {
            const previewPattern: CodePattern = {
              parts: preset.parts,
              separator: pattern.separator,
              uppercase: pattern.uppercase,
            };
            const preview = generatePreviewCode(previewPattern, previewContext);
            return (
              <RadioCardItem
                key={preset.id}
                value={preset.id}
                cursor="pointer"
                label={
                  <Text fontFamily="mono" fontSize="md" fontWeight="semibold">
                    {preview}
                  </Text>
                }
                description={t(`${prefix}.preset_${preset.id}`)}
              />
            );
          })}
        </VStack>
      </RadioCardRoot>

      <VStack gap={3} align="stretch">
        <PatternOptionSwitch
          label={t(`${prefix}.separatorDashes`)}
          checked={separated}
          onCheckedChange={(checked) =>
            onChange({
              ...pattern,
              separator: checked ? CodePatternSeparator.Dash : CodePatternSeparator.None,
            })
          }
        />

        <PatternOptionSwitch
          label={t(`${prefix}.uppercaseCodes`)}
          checked={pattern.uppercase}
          onCheckedChange={(uppercase) => onChange({ ...pattern, uppercase })}
        />
      </VStack>
    </VStack>
  );
};

export default DiscountCodePatternBuilder;
